import React, { useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import AutoSizer from "react-virtualized/dist/commonjs/AutoSizer";
import KeplerGl from "@kepler.gl/components";
import { processCsvData } from '@kepler.gl/processors';
import { addDataToMap } from '@kepler.gl/actions';
import LoadingContent from '../../../components/Animations/LoadingContent';

const MAPBOX_TOKEN = "pk.eyJ1Ijoiam9zZS12YWxsZWpvczE0MTIiLCJhIjoiY205YWZqajQzMDRwYjJxcTQyZ2dxZGZ1cSJ9.z7BDDJD9HcS6vz_mk-EAsw";

const keplerConfig = {
  "version": "v1",
  "config": {
    "visState": {
      "filters": [
        {
          "dataId": [
            "gps_data"
          ],
          "id": "time-filter",
          "name": [
            "event_time"
          ],
          "type": "timeRange",
          "value": [
            0,
            0
          ],
          "enlarged": true,
          "plotType": {
            "type": "histogram",
            "interval": "15-minute",
            "defaultTimeFormat": "L LT",
            "aggregation": "sum"
          },
          "animationWindow": "free",
          "yAxis": null,
          "speed": 1,
          "enabled": true
        }
      ],
      "layers": [
        {
          "id": "gps-points",
          "type": "point",
          "config": {
            "dataId": "gps_data",
            "columnMode": "points",
            "label": "GPS Points",
            "color": [
              119,
              110,
              87
            ],
            "highlightColor": [
              252,
              242,
              26,
              255
            ],
            "columns": {
              "lat": "lat",
              "lng": "lon"
            },
            "isVisible": true,
            "visConfig": {
              "radius": 10,
              "fixedRadius": false,
              "opacity": 0.8,
              "outline": false,
              "thickness": 2,
              "strokeColor": null,
              "colorRange": {
                "name": "Global Warming",
                "type": "sequential",
                "category": "Uber",
                "colors": [
                  "#5A1846",
                  "#900C3F",
                  "#C70039",
                  "#E3611C",
                  "#F1920E",
                  "#FFC300"
                ]
              },
              "radiusRange": [
                0,
                50
              ],
              "filled": true,
              "billboard": false,
              "allowHover": true,
              "showNeighborOnHover": false,
              "showHighlightColor": true
            },
            "hidden": false,
            "textLabel": []
          },
          "visualChannels": {
            "colorField": {
              "name": "kph",
              "type": "real"
            },
            "colorScale": "quantile",
            "strokeColorField": null,
            "strokeColorScale": "quantile",
            "sizeField": null,
            "sizeScale": "linear"
          }
        }
      ],
      "interactionConfig": {
        "tooltip": {
          "fieldsToShow": {
            "gps_data": [
              {
                "name": "event_time",
                "format": null
              },
              {
                "name": "primary_name",
                "format": null
              },
              {
                "name": "kph",
                "format": null
              },
              {
                "name": "head",
                "format": null
              }
            ]
          },
          "compareMode": false,
          "compareType": "absolute",
          "enabled": true
        },
        "brush": {
          "size": 0.5,
          "enabled": false
        },
        "geocoder": {
          "enabled": false
        },
        "coordinate": {
          "enabled": false
        }
      },
      "layerBlending": "normal",
      "overlayBlending": "normal",
      "splitMaps": [],
      "animationConfig": {
        "currentTime": null,
        "speed": 1
      }
    },
    "mapState": {
      "bearing": 0,
      "dragRotate": false,
      "latitude": -22.410062691949843,
      "longitude": -68.83240581462148,
      "pitch": 0,
      "zoom": 10.881460770267863,
      "isSplit": false,
      "isViewportSynced": true,
      "isZoomLocked": false
    },
    "mapStyle": {
      "styleType": "dark",
      "topLayerGroups": {},
      "visibleLayerGroups": {
        "label": true,
        "road": true,
        "border": false,
        "building": true,
        "water": true,
        "land": true,
        "3d building": false
      },
      "threeDBuildingColor": [
        15.035172933000911,
        15.035172933000911,
        15.035172933000911
      ],
      "backgroundColor": [
        0,
        0,
        0
      ],
      "mapStyles": {}
    }
  }
};

const CycleAnomalyMap = () => {
  const dispatch = useDispatch();
  const { gpsPoints } = useSelector(state => state.CycleAnomaly);

  useEffect(() => {
    if (gpsPoints.data && gpsPoints.data.length > 0) {
      
      // Transform GPS data to CSV format for Kepler
      const csvData = convertToCSV(gpsPoints.data);
      
      const data = processCsvData(csvData);
      
      const datasets = [
        {
          data: data,
          info: {
            id: 'gps_data',
            label: `GPS Data - ${gpsPoints.patente}`
          }
        }
      ];

      dispatch(addDataToMap({ 
        datasets: datasets, 
        config: keplerConfig,
        options: {
          centerMap: true
        }
      }));
    }
  }, [gpsPoints.data, dispatch]);

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    
    console.log('Sample GPS point:', data[0]);
    
    // Map the actual column names from your API response
    const headers = ['event_time', 'primary_name', 'lat', 'lon', 'kph', 'head'];
    
    // Create CSV rows - map your API fields to CSV columns
    const rows = data.map(row => {
      return [
        row.event_time || '',
        row.primary_name || '',
        row.latitude || row.lat || '',
        row.longitude || row.lon || '',
        row.speed || row.kph || '',
        row.heading || row.head || ''
      ].join(',');
    });
    
    // Combine headers and rows
    return [headers.join(','), ...rows].join('\n');
  };

  return (
    <>
      {gpsPoints.loading && <LoadingContent />}
      
      {/* Always show the Kepler map */}
      {!gpsPoints.loading && (
        <div style={{ height: "70vh", width: "100%" }}>
          <AutoSizer>
            {({ height, width }) => (
              <KeplerGl
                id="cycle-anomaly-map"
                mapboxApiAccessToken={MAPBOX_TOKEN}
                width={width}
                height={height}
              />
            )}
          </AutoSizer>
        </div>
      )}
      
      {!gpsPoints.loading && gpsPoints.error && (
        <div style={{ padding: '20px', color: 'red' }}>
          Error loading GPS data: {gpsPoints.error}
        </div>
      )}
    </>
  );
};

export default CycleAnomalyMap;